import { FC, ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ReadablePalette } from '../common/Color';
import { u } from '../common/Units';
import { ChevronDownIcon } from './DMIcons';

export interface DMDropdownOption {
  value: string,
  label: string,
  count?: number
}

/** A dropdown in the toolbar. The first option is the "all"/default option with an empty value */
export interface DMDropdown {
  id: string,
  icon: ReactElement,
  label: string,
  options: DMDropdownOption[],
  /** Selected value. Undefined when the first option is selected */
  selected?: string,
  onSelect: (value: string) => void
}

interface DMToolbarProps {
  palette: ReadablePalette,
  /** Hit count text. Not shown if undefined */
  hitCount?: string,
  filters: DMDropdown[],
  /** Shows a "Fjern filtre" button */
  onReset?: () => void,
  /** Sort dropdown, aligned to the right */
  sort?: DMDropdown
}

const Wrapper = styled.div<{ $palette: ReadablePalette }>`
  position: relative;
  z-index: 3;
  margin: 0 ${u(65)} ${u(24)};
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${u(16)};
  color: ${p => p.$palette.textColor};
`;

const HitCount = styled.p<{ $palette: ReadablePalette }>`
  margin: 0 ${u(24)} 0 0;
  color: ${p => p.$palette.mutedTextColor};
  white-space: nowrap;
`;

const ResetButton = styled.button`
  height: ${u(44)};
  padding: 0 ${u(16)};
  border: none;
  border-radius: ${u(22)};
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 0.85em;
  text-decoration: underline;
  text-underline-offset: 0.2em;
  cursor: pointer;
  white-space: nowrap;
`;

const DropdownWrapper = styled.div`
  position: relative;

  &.alignRight {
    margin-left: auto;
  }
`;

const DropdownButton = styled.button<{ $palette: ReadablePalette }>`
  display: flex;
  align-items: center;
  gap: ${u(12)};
  height: ${u(44)};
  padding: 0 ${u(16)} 0 ${u(18)};
  border-radius: ${u(22)};
  border: 1px solid ${p => p.$palette.borderColor};
  background-color: ${p => p.$palette.controlColor};
  color: inherit;
  font: inherit;
  font-size: 0.85em;
  cursor: pointer;
  white-space: nowrap;

  &.active {
    border-color: ${p => p.$palette.textColor};
  }

  & svg {
    width: ${u(20)};
    height: ${u(20)};
    flex: 0 0 auto;
  }

  & .label {
    max-width: ${u(260)};
    overflow: hidden;
    text-overflow: ellipsis;
  }

  & .chevron {
    transition: transform 0.2s;
  }

  &[aria-expanded="true"] .chevron {
    transform: rotate(180deg);
  }
`;

const DropdownPanel = styled.ul<{ $palette: ReadablePalette }>`
  position: absolute;
  z-index: 1;
  top: calc(100% + ${u(8)});
  left: 0;
  min-width: 100%;
  max-height: ${u(460)};
  overflow-y: auto;
  margin: 0;
  padding: ${u(8)} 0;
  list-style: none;
  border-radius: ${u(16)};
  background-color: ${p => p.$palette.surfaceColor};
  color: ${p => p.$palette.textColor};
  box-shadow: 0 ${u(12)} ${u(40)} rgba(0, 0, 0, 0.35);
  box-sizing: border-box;

  .alignRight > & {
    left: auto;
    right: 0;
  }

  & button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${u(24)};
    width: 100%;
    padding: ${u(14)} ${u(22)};
    border: none;
    background: transparent;
    color: inherit;
    font: inherit;
    font-size: 0.85em;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
  }

  & button.selected {
    font-weight: 600;
    background-color: ${p => p.$palette.cardColor};
  }

  & .count {
    color: ${p => p.$palette.mutedTextColor};
    font-weight: normal;
  }
`;

interface DropdownProps {
  dropdown: DMDropdown,
  palette: ReadablePalette,
  isOpen: boolean,
  alignRight?: boolean,
  onToggle: () => void,
  onClose: () => void
}

/**
 * Touch friendly dropdown
 * @returns {JSX.Element} Component template
 */
const Dropdown: FC<DropdownProps> = ({ dropdown, palette, isOpen, alignRight, onToggle, onClose }) => {
  const { id, icon, label, options, selected = "", onSelect } = dropdown;
  return (
    <DropdownWrapper className={alignRight ? "alignRight" : undefined} data-dropdown={id}>
      <DropdownButton $palette={palette} className={selected ? "active" : undefined} onClick={onToggle} aria-expanded={isOpen} aria-haspopup="listbox">
        {icon}
        <span className="label">{label}</span>
        <ChevronDownIcon className="chevron"/>
      </DropdownButton>
      {isOpen && (
        <DropdownPanel $palette={palette} role="listbox">
          {options.map(option => (
            <li key={option.value || "all"}>
              <button className={selected === option.value ? "selected" : undefined} role="option" aria-selected={selected === option.value} onClick={() => { onSelect(option.value); onClose(); }}>
                <span>{option.label}</span>
                {typeof option.count === "number" && <span className="count">{option.count.toLocaleString("nb-NO")}</span>}
              </button>
            </li>
          ))}
        </DropdownPanel>
      )}
    </DropdownWrapper>
  );
}

/**
 * Toolbar of the DM view: hit count, filter dropdowns, a reset button and the sort dropdown.
 * Only one dropdown is open at a time; tapping outside closes it.
 * @returns {JSX.Element} Component template
 */
const DMToolbar: FC<DMToolbarProps> = ({ palette, hitCount, filters, onReset, sort }) => {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);

  // Close the open dropdown when tapping outside it
  useEffect(() => {
    if (!openId) { return; }
    const onPointerDown = (e: Event) => {
      !(e.target as HTMLElement)?.closest?.(`[data-dropdown="${openId}"]`) && setOpenId(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [openId]);

  const renderDropdown = (dropdown: DMDropdown, alignRight?: boolean) => (
    <Dropdown key={dropdown.id} dropdown={dropdown} palette={palette} alignRight={alignRight} isOpen={openId === dropdown.id}
      onToggle={() => setOpenId(prev => prev === dropdown.id ? null : dropdown.id)} onClose={() => setOpenId(null)}/>
  );

  return (
    <Wrapper $palette={palette}>
      {hitCount !== undefined && <HitCount $palette={palette} aria-live="polite">{hitCount}</HitCount>}
      {filters.map(filter => renderDropdown(filter))}
      {onReset && <ResetButton onClick={() => { onReset(); setOpenId(null); }}>{t("dm.filter.reset")}</ResetButton>}
      {sort && renderDropdown(sort, true)}
    </Wrapper>
  );
}

export default DMToolbar;
